import type { ReactNode } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useProfileDropdown } from "@hooks/useProfileDropdown";
import LanguageSwitcher from "@components/common/LanguageSwitcher";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import ChangePasswordModal from "@features/ChangePassword/ChangePasswordModal";
import { useTranslation } from "react-i18next";
import ProfileModal from "@features/Profile/components";
import AddSkillsModal from "@features/Profile/components/AddSkillsModal";
import { UserModal } from "@components/common/Modal/UserModal";
import { LogoutConfirmationModal } from "@components/Auth";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@store/store";
import { openProfileCompletionModal } from "@store/slices/modalSlice";
import { PORTAL_PROFILE_MODAL_WIDTH_PERCENT } from "@constants/portalUiTheme";
import { useProfile } from "./services/GetProfile";

interface ProfileDropdownProps {
	avatar: ReactNode;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ avatar }) => {
	const dispatch = useDispatch();
	const { user } = useSelector((state: RootState) => state.user);
	const isProfileComplete = user?.isProfileComplete ?? false;

	const {
		showMenu,
		menuRef,
		handleToggle,
		handleLogout,
		handleLogoutConfirm,
		handleLogoutCancel,
		showLogoutModal,
		ShowProfile,
		HideProfile,
		showProfileModal,
		handleViewRegistration,
		showAddSkillsModal,
		ShowAddSkills,
		HideAddSkills,
		ShowChangePassword,
		HideChangePassword,
		showChangePasswordModal,
	} = useProfileDropdown();
	const { t } = useTranslation();
	const { data: profileData } = useProfile();
	const profileLabel =
		[profileData?.firstName, profileData?.lastName].filter(Boolean).join(" ").trim() ||
		t("psdaCommon.profile.profileTitle");

	const ExpandIcon = showMenu ? (
		<ExpandLessIcon className="text-gray-400" />
	) : (
		<ExpandMoreIcon className="text-gray-400" />
	);

	return (
		<div className="relative" ref={menuRef}>
			<div className="header-profile-pill" aria-label={profileLabel}>
				<div
					className="header-profile-avatar"
					role="button"
					tabIndex={0}
					onClick={handleToggle}
					onKeyDown={event => {
						if (event.key === "Enter" || event.key === " ") {
							event.preventDefault();
							handleToggle();
						}
					}}
					aria-label={profileLabel}
					aria-expanded={showMenu}
					aria-haspopup="menu">
					{avatar}
				</div>
				<div className="header-profile-chevron">
					<div className="relative">
						<button
							type="button"
							onClick={handleToggle}
							aria-label={t("psdaCommon.profile.profileTitle")}
							aria-expanded={showMenu}>
							{ExpandIcon}
						</button>
					</div>
				</div>
			</div>
			{showMenu && (
				<div
					className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-50 p-4"
					role="menu"
					aria-label={t("common.profileMenu.menuLabel")}>
					<div
						className="flex items-center gap-2 mb-2 text-gray-900 font-medium cursor-pointer hover:bg-accent-40 hover:text-accent p-2 rounded-lg transition-colors duration-150"
						role="menuitem"
						onClick={() => {
							if (!isProfileComplete) {
								dispatch(openProfileCompletionModal());
								return;
							}
							ShowProfile();
						}}>
						<PersonOutlineOutlinedIcon color="info" sx={{ fontSize: "24px" }} />
						{t("psdaCommon.profile.profileTitle")}
					</div>
					<div
						className="flex items-center gap-2 mb-2 text-gray-900 font-medium cursor-pointer hover:bg-accent-40 hover:text-accent p-2 rounded-lg transition-colors duration-150"
						role="menuitem"
						onClick={ShowChangePassword}>
						<KeyOutlinedIcon color="info" sx={{ fontSize: "24px" }} />
						{t("psdaCommon.profile.changePassword.menuLabel")}
					</div>
					<div
						className="flex items-center gap-2 mb-2 text-gray-900 font-medium cursor-pointer hover:bg-accent-40 hover:text-accent p-2 rounded-lg transition-colors duration-150"
						role="menuitem"
						onClick={handleViewRegistration}>
						<DescriptionOutlinedIcon color="info" sx={{ fontSize: "24px" }} />
						{t("common.profileMenu.viewRegistration")}
					</div>
					<div
						className="flex items-center gap-2 mb-2 text-gray-900 font-medium cursor-pointer hover:bg-accent-40 hover:text-accent p-2 rounded-lg transition-colors duration-150"
						role="menuitem"
						onClick={ShowAddSkills}>
						<AddCircleOutlineIcon color="info" sx={{ fontSize: "24px" }} />
						{t("common.profileMenu.addSkills")}
					</div>
					<div className="h-[1px] bg-gray-200 my-2" role="separator" aria-orientation="horizontal" />
					<div className="mb-2" role="menuitem">
						<LanguageSwitcher />
					</div>
					<div className="h-[1px] bg-gray-200 my-2" role="separator" aria-orientation="horizontal" />
					<button
						className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-red-50 text-red-500 mt-2 hover:bg-red-100 transition"
						onClick={handleLogout}
						role="menuitem"
						type="button">
						<LogoutOutlinedIcon color="error" sx={{ fontSize: "2wpx" }} />
						{t("psdaCommon.common.buttons.logout")}
					</button>
				</div>
			)}
			{showProfileModal && (
				<UserModal
					heigth={100}
					width={PORTAL_PROFILE_MODAL_WIDTH_PERCENT}
					show={showProfileModal}
					onClose={HideProfile}
					children={<ProfileModal HideProfile={HideProfile} />}
				/>
			)}
			<LogoutConfirmationModal show={showLogoutModal} onConfirm={handleLogoutConfirm} onCancel={handleLogoutCancel} />
			<AddSkillsModal open={showAddSkillsModal} onClose={HideAddSkills} />
			<ChangePasswordModal isOpen={showChangePasswordModal} onClose={HideChangePassword} />
		</div>
	);
};
export default ProfileDropdown;
